import * as i0 from '@angular/core';
import { forwardRef, Directive, Input, NgModule } from '@angular/core';
import { Validators, NG_VALIDATORS } from '@angular/forms';
import { delay } from 'rxjs/operators';

class AbstractControlUtil {
    static isNotPresent(control) {
        const value = control.value;
        if (value === undefined || value === null) {
            return true;
        }
        return value !== "" ? false : true;
    }
    static addError(control, errorId, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value) {
        if (!control) {
            return;
        }
        if (!control.errors) {
            control.setErrors({ [errorId]: value });
        }
        else if (!control.hasError(errorId)) {
            control.errors[errorId] = value;
        }
    }
    static removeError(control, errorId) {
        if (!control) {
            return;
        }
        if (control.errors && control.hasError(errorId)) {
            if (Object.keys(control.errors).length > 1) {
                delete control.errors[errorId];
            }
            else {
                control.setErrors(null);
            }
        }
    }
}

// tslint:disable-next-line:variable-name
class PasswordValidators {
    static repeatCharacterRegexRule(repeatCount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const repeatDec = repeatCount - 1;
            const pattern = "([^\\x00-\\x1F])\\1{" + repeatDec + "}";
            if (control.value !== "" && new RegExp(pattern).test(control.value)) {
                return { repeatCharacterRegexRule: { repeatCount } };
            }
            return null;
        };
        return validator;
    }
    static allowedCharacterRule(allowedChars) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            let valid = true;
            const invalidChars = [];
            for (const char of value) {
                if (allowedChars.indexOf(char) === -1) {
                    valid = false;
                    if (invalidChars.indexOf(char) === -1) {
                        invalidChars.push(char);
                    }
                }
            }
            if (!valid) {
                return {
                    allowedCharacterRule: {
                        invalidChars,
                        allowedChars,
                    },
                };
            }
            return null;
        };
        return validator;
    }
    static alphabeticalCharacterRule(amount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length === 0) {
                return null;
            }
            const pattern = /[^A-Za-z]+/g;
            const stripped = value.replace(pattern, "");
            if (stripped.length < amount) {
                return {
                    alphabeticalCharacterRule: {
                        required: amount,
                        actual: stripped.length,
                    },
                };
            }
            return null;
        };
        return validator;
    }
    static digitCharacterRule(amount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length === 0) {
                return null;
            }
            const pattern = /[^0-9.]+/g;
            const stripped = value.replace(pattern, "");
            if (stripped.length < amount) {
                return {
                    digitCharacterRule: { required: amount, actual: stripped.length },
                };
            }
            return null;
        };
        return validator;
    }
    static lowercaseCharacterRule(amount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length === 0) {
                return null;
            }
            const pattern = /[^a-z]+/g;
            const stripped = value.replace(pattern, "");
            if (stripped.length < amount) {
                return {
                    lowercaseCharacterRule: { required: amount, actual: stripped.length },
                };
            }
            return null;
        };
        return validator;
    }
    static uppercaseCharacterRule(amount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length === 0) {
                return null;
            }
            const pattern = /[^A-Z]+/g;
            const stripped = value.replace(pattern, "");
            if (stripped.length < amount) {
                return {
                    uppercaseCharacterRule: { required: amount, actual: stripped.length },
                };
            }
            return null;
        };
        return validator;
    }
    static specialCharacterRule(amount) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length === 0) {
                return null;
            }
            const pattern = /[\w\s]+/g;
            const stripped = value.replace(pattern, "");
            if (stripped.length < amount) {
                return {
                    specialCharacterRule: { required: amount, actual: stripped.length },
                };
            }
            return null;
        };
        return validator;
    }
    static mismatchedPasswords(passwordControlName, confirmPasswordControlName) {
        const validator = (group) => {
            var _a, _b;
            const newPasswordValue = (_a = group.get(passwordControlName ? passwordControlName : "newPassword")) === null || _a === void 0 ? void 0 : _a.value;
            const newPasswordConfirmValue = (_b = group.get(confirmPasswordControlName ? confirmPasswordControlName : "confirmPassword")) === null || _b === void 0 ? void 0 : _b.value;
            if (newPasswordValue !== newPasswordConfirmValue) {
                AbstractControlUtil.addError(group.get(confirmPasswordControlName ? confirmPasswordControlName : "confirmPassword"), "mismatchedPasswords", true);
                return { mismatchedPasswords: true };
            }
            else {
                AbstractControlUtil.removeError(group.get(confirmPasswordControlName ? confirmPasswordControlName : "confirmPassword"), "mismatchedPasswords");
            }
            return null;
        };
        return validator;
    }
}

/*
 * Code fromMailcheck https://github.com/mailcheck/mailcheck
 * Author
 * Derrick Ko (@derrickko)
 *
 * Released under the MIT License.
 *
 * v 1.1.2
 */
class EmailSuggestion {
    constructor() {
        this.defaultOptions = {
            domains: [
                "msn.com",
                "bellsouth.net",
                "telus.net",
                "comcast.net",
                "optusnet.com.au",
                "earthlink.net",
                "qq.com",
                "sky.com",
                "icloud.com",
                "mac.com",
                "sympatico.ca",
                "googlemail.com",
                "att.net",
                "xtra.co.nz",
                "web.de",
                "cox.net",
                "gmail.com",
                "ymail.com",
                "yahoo.com",
                "aim.com",
                "rogers.com",
                "verizon.net",
                "rocketmail.com",
                "google.com",
                "optonline.net",
                "sbcglobal.net",
                "aol.com",
                "me.com",
                "btinternet.com",
                "charter.net",
                "shaw.ca",
            ],
            secondLevelDomains: ["yahoo", "hotmail", "mail", "live", "outlook", "gmx"],
            topLevelDomains: [
                "com",
                "com.au",
                "com.tw",
                "ca",
                "co.nz",
                "co.uk",
                "de",
                "fr",
                "it",
                "ru",
                "net",
                "org",
                "edu",
                "gov",
                "jp",
                "nl",
                "kr",
                "se",
                "eu",
                "ie",
                "co.il",
                "us",
                "at",
                "be",
                "dk",
                "hk",
                "es",
                "gr",
                "ch",
                "no",
                "cz",
                "in",
                "net",
                "net.au",
                "info",
                "biz",
                "mil",
                "co.jp",
                "sg",
                "hu",
                "uk",
            ],
        };
    }
    suggest(email, options) {
        var _a;
        let opt = this.defaultOptions;
        if (options !== undefined) {
            opt = options;
        }
        const emailParts = this.splitEmail(email.toLowerCase());
        if (!emailParts) {
            return null;
        }
        if (opt.secondLevelDomains && opt.topLevelDomains) {
            // If the email is a valid 2nd-level + top-level, do not suggest anything.
            if (opt.secondLevelDomains.indexOf(emailParts.secondLevelDomain) !== -1 &&
                opt.topLevelDomains.indexOf(emailParts.topLevelDomain) !== -1) {
                return null;
            }
        }
        let closestDomain = this.findClosestDomain((_a = emailParts.domain) !== null && _a !== void 0 ? _a : "", opt.domains, 2);
        if (closestDomain) {
            if (closestDomain === emailParts.domain) {
                // The email address exactly matches one of the supplied domains; do not return a suggestion.
                return null;
            }
            else {
                // The email address closely matches one of the supplied domains; return a suggestion
                return {
                    suggestion: {
                        address: emailParts.address,
                        domain: closestDomain,
                        full: emailParts.address + "@" + closestDomain,
                    },
                };
            }
        }
        const closestSecondLevelDomain = this.findClosestDomain(emailParts.secondLevelDomain, opt.secondLevelDomains, 2);
        const closestTopLevelDomain = this.findClosestDomain(emailParts.topLevelDomain, opt.topLevelDomains, 2);
        if (emailParts.domain) {
            closestDomain = emailParts.domain;
            let rtrn = false;
            if (closestSecondLevelDomain && closestSecondLevelDomain !== emailParts.secondLevelDomain) {
                // The email address may have a mispelled second-level domain; return a suggestion
                closestDomain = closestDomain.replace(emailParts.secondLevelDomain, closestSecondLevelDomain);
                rtrn = true;
            }
            if (closestTopLevelDomain &&
                closestTopLevelDomain !== emailParts.topLevelDomain &&
                emailParts.secondLevelDomain !== "") {
                // The email address may have a mispelled top-level domain; return a suggestion
                closestDomain = closestDomain.replace(new RegExp(emailParts.topLevelDomain + "$"), closestTopLevelDomain);
                rtrn = true;
            }
            if (rtrn) {
                return {
                    suggestion: {
                        address: emailParts.address,
                        domain: closestDomain,
                        full: emailParts.address + "@" + closestDomain,
                    },
                };
            }
        }
        /* The email address exactly matches one of the supplied domains, does not closely
         * match any domain and does not appear to simply have a mispelled top-level domain,
         * or is an invalid email address; do not return a suggestion.
         */
        return null;
    }
    splitEmail(email) {
        var _a;
        const parts = email.trim().split("@");
        if (parts.length < 2) {
            return null;
        }
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < parts.length; i++) {
            if (parts[i] === "") {
                return null;
            }
        }
        const result = {
            topLevelDomain: "",
            secondLevelDomain: "",
            domain: parts.pop(),
            address: "",
        };
        const domainParts = (_a = result.domain) === null || _a === void 0 ? void 0 : _a.split(".");
        if (!domainParts) {
            return null;
        }
        if (domainParts.length === 0) {
            return null;
        }
        else if (domainParts.length === 1) {
            result.topLevelDomain = domainParts[0];
        }
        else {
            // The address has a domain and a top-level domain
            result.secondLevelDomain = domainParts[0];
            for (let j = 1; j < domainParts.length; j++) {
                result.topLevelDomain += domainParts[j] + ".";
            }
            result.topLevelDomain = result.topLevelDomain.substring(0, result.topLevelDomain.length - 1);
        }
        result.address = parts.join("@");
        return result;
    }
    findClosestDomain(domain, domains, threshold) {
        let dist;
        let minDist = Infinity;
        let closestDomain = null;
        if (!domain || !domains) {
            return null;
        }
        // tslint:disable-next-line: prefer-for-of
        for (let i = 0; i < domains.length; i++) {
            if (domain === domains[i]) {
                return domain;
            }
            dist = this.sift4Distance(domain, domains[i], 5);
            if (dist < minDist) {
                minDist = dist;
                closestDomain = domains[i];
            }
        }
        if (minDist <= threshold && closestDomain !== null) {
            return closestDomain;
        }
        else {
            return null;
        }
    }
    sift4Distance(s1, s2, maxOffset) {
        // sift4: https://siderite.blogspot.com/2014/11/super-fast-and-accurate-string-distance.html
        if (maxOffset === null) {
            maxOffset = 5; // default
        }
        if (!s1 || !s1.length) {
            if (!s2) {
                return 0;
            }
            return s2.length;
        }
        if (!s2 || !s2.length) {
            return s1.length;
        }
        const l1 = s1.length;
        const l2 = s2.length;
        let c1 = 0; // cursor for string 1
        let c2 = 0; // cursor for string 2
        let lcss = 0; // largest common subsequence
        let localCS = 0; // local common substring
        let trans = 0; // number of transpositions ('ab' vs 'ba')
        const offsetArr = []; // offset pair array, for computing the transpositions
        while (c1 < l1 && c2 < l2) {
            if (s1.charAt(c1) === s2.charAt(c2)) {
                localCS++;
                let isTrans = false;
                // see if current match is a transposition
                let i = 0;
                while (i < offsetArr.length) {
                    const ofs = offsetArr[i];
                    if (c1 <= ofs.c1 || c2 <= ofs.c2) {
                        // when two matches cross, the one considered a transposition is the one with the largest difference in offsets
                        isTrans = Math.abs(c2 - c1) >= Math.abs(ofs.c2 - ofs.c1);
                        if (isTrans) {
                            trans++;
                        }
                        else {
                            if (!ofs.trans) {
                                ofs.trans = true;
                                trans++;
                            }
                        }
                        break;
                    }
                    else {
                        if (c1 > ofs.c2 && c2 > ofs.c1) {
                            offsetArr.splice(i, 1);
                        }
                        else {
                            i++;
                        }
                    }
                }
                offsetArr.push({
                    c1,
                    c2,
                    trans: isTrans,
                });
            }
            else {
                lcss += localCS;
                localCS = 0;
                if (c1 !== c2) {
                    c1 = c2 = Math.min(c1, c2); // using min allows the computation of transpositions
                }
                // if matching characters are found, remove 1 from both cursors (they get incremented at the end of the loop)
                // so that we can have only one code block handling matches
                for (let j = 0; j < maxOffset && (c1 + j < l1 || c2 + j < l2); j++) {
                    if (c1 + j < l1 && s1.charAt(c1 + j) === s2.charAt(c2)) {
                        c1 += j - 1;
                        c2--;
                        break;
                    }
                    if (c2 + j < l2 && s1.charAt(c1) === s2.charAt(c2 + j)) {
                        c1--;
                        c2 += j - 1;
                        break;
                    }
                }
            }
            c1++;
            c2++;
            // this covers the case where the last match is on the last token in list, so that it can compute transpositions correctly
            if (c1 >= l1 || c2 >= l2) {
                lcss += localCS;
                localCS = 0;
                c1 = c2 = Math.min(c1, c2);
            }
        }
        lcss += localCS;
        return Math.round(Math.max(l1, l2) - lcss + trans); // add the cost of transpositions to the final result
    }
}

class EmailValidators {
    static simple(control) {
        if (AbstractControlUtil.isNotPresent(control)) {
            return null;
        }
        const pattern = /.+@.+\..+/i;
        if (pattern.test(control.value)) {
            return null;
        }
        return { simpleEmailRule: true };
    }
    // https://www.w3.org/TR/html5/forms.html#valid-e-mail-address
    static normal(control) {
        if (AbstractControlUtil.isNotPresent(control)) {
            return null;
        }
        const pattern = 
        // tslint:disable-next-line:max-line-length
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        if (pattern.test(control.value)) {
            return null;
        }
        return { normalEmailRule: true };
    }
    static suggest(options) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control)) {
                return null;
            }
            return this.emailSuggestion.suggest(control.value, options);
        };
        return validator;
    }
}
EmailValidators.emailSuggestion = new EmailSuggestion();

class UniversalValidators {
    static noWhitespace(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        const pattern = "\\s";
        if (new RegExp(pattern).test(control.value)) {
            return { noWhitespaceRequired: true };
        }
        return null;
    }
    static noEmptyString(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (control.value.trim().length === 0) {
            return { noEmptyString: true };
        }
        return null;
    }
    static isNumber(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (isNaN(control.value)) {
            return { numberRequired: true };
        }
        return null;
    }
    static isInRange(minValue, maxValue) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            if (isNaN(control.value)) {
                return { numberRequired: true };
            }
            if (+control.value < minValue) {
                return {
                    rangeValueToSmall: {
                        requiredMinValue: minValue,
                        requiredMaxValue: maxValue,
                        actual: control.value,
                    },
                };
            }
            if (+control.value > maxValue) {
                return {
                    rangeValueToBig: {
                        requiredMinValue: minValue,
                        requiredMaxValue: maxValue,
                        actual: control.value,
                    },
                };
            }
            else {
                return null;
            }
        };
        return validator;
    }
    static minLength(minLength) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (value.length >= minLength) {
                return null;
            }
            return {
                minLength: { requiredMinLength: minLength, actualLength: value.length },
            };
        };
        return validator;
    }
    static maxLength(maxLength) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (maxLength >= value.length) {
                return null;
            }
            return {
                maxLength: { requiredMaxLength: maxLength, actualLength: value.length },
            };
        };
        return validator;
    }
    static min(min) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (isNaN(control.value)) {
                return { numberRequired: true };
            }
            if (+value >= min) {
                return null;
            }
            return { min: { required: min, actual: control.value } };
        };
        return validator;
    }
    static max(max) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const value = control.value;
            if (isNaN(control.value)) {
                return { numberRequired: true };
            }
            if (max >= +value) {
                return null;
            }
            return { max: { required: max, actual: control.value } };
        };
        return validator;
    }
    static minDate(minDate) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const date = new Date(control.value);
            if (isNaN(date.getTime())) {
                return { dateRequired: true };
            }
            if (date.getTime() >= minDate.getTime()) {
                return null;
            }
            return { minDate: { required: minDate, actual: date } };
        };
        return validator;
    }
    static maxDate(minDate) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const date = new Date(control.value);
            if (isNaN(date.getTime())) {
                return { dateRequired: true };
            }
            if (date.getTime() <= minDate.getTime()) {
                return null;
            }
            return { maxDate: { required: minDate, actual: date } };
        };
        return validator;
    }
    /**
     * @description
     * Validator that requires the control's type to equal to the provided type.
     *
     * @usageNotes
     *
     * ### Validate against a string type
     *
     * ```typescript
     * const control = new FormControl(2, Validators.type('string'));
     *
     * console.log(control.errors); // {type: {required: 'string', actual: 'number'}}
     * ```
     *
     * @returns A validator function that returns an error map with the
     * `type` property if the validation check fails, otherwise `null`.
     *
     */
    static type(type) {
        const validator = (control) => {
            if (AbstractControlUtil.isNotPresent(control))
                return null;
            const typeOfControl = typeof control.value;
            return typeOfControl !== type ? { type: { required: type, actual: typeOfControl } } : null;
        };
        return validator;
    }
}

const visaRegex = "^(?:4[0-9]{12})(?:[0-9]{3})?$";
const americanExpressRegex = "^(?:3[47][0-9]{13})$";
const maestroRegex = "^(?:(?:5[0678]\\d\\d|6304|6390|67\\d\\d)\\d{8,15})$";
const jcbRegex = "^(?:(?:2131|1800|35\\d{3})\\d{11})$";
const discoverRegex = "^(?:6(?:011|5[0-9]{2})(?:[0-9]{12}))$";
const dinersclubRegex = "^(?:3(?:0[0-5]|[68][0-9])[0-9]{11})$";
const mastercardRegex = "^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$";
class CreditCardValidators {
    static isCreditCard(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(americanExpressRegex +
            "|" +
            visaRegex +
            "|" +
            maestroRegex +
            "|" +
            jcbRegex +
            "|" +
            discoverRegex +
            "|" +
            mastercardRegex +
            "|" +
            dinersclubRegex).test(control.value)) {
            return null;
        }
        return { creditcard: true };
    }
    static americanExpress(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(americanExpressRegex).test(control.value)) {
            return null;
        }
        return { americanExpress: true };
    }
    static dinersclub(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(dinersclubRegex).test(control.value)) {
            return null;
        }
        return { dinersclub: true };
    }
    static discover(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(discoverRegex).test(control.value)) {
            return null;
        }
        return { discover: true };
    }
    static jcb(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(jcbRegex).test(control.value)) {
            return null;
        }
        return { jcb: true };
    }
    static maestro(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(maestroRegex).test(control.value)) {
            return null;
        }
        return { maestro: true };
    }
    static mastercard(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(mastercardRegex).test(control.value)) {
            return null;
        }
        return { mastercard: true };
    }
    static visa(control) {
        if (AbstractControlUtil.isNotPresent(control))
            return null;
        if (new RegExp(visaRegex).test(control.value)) {
            return null;
        }
        return { visa: true };
    }
}

class PasswordValidatorDirective {
    constructor() {
        this.repeatCharacter = 4;
        this.alphabeticalCharacter = 1;
        this.digitCharacter = 1;
        this.lowercaseCharacter = 1;
        this.uppercaseCharacter = 1;
    }
    ngOnInit() {
        this.repeatCharacterValidator = PasswordValidators.repeatCharacterRegexRule(this.repeatCharacter);
        this.alphabeticalCharacterValidator = PasswordValidators.alphabeticalCharacterRule(this.alphabeticalCharacter);
        this.digitCharacterValidator = PasswordValidators.digitCharacterRule(this.digitCharacter);
        this.lowercaseCharacterValidator = PasswordValidators.lowercaseCharacterRule(this.lowercaseCharacter);
        this.uppercaseCharacterValidator = PasswordValidators.uppercaseCharacterRule(this.uppercaseCharacter);
    }
    ngOnChanges(changes) {
        let inputChanged = false;
        if (changes["repeatCharacter"]) {
            this.repeatCharacterValidator = PasswordValidators.repeatCharacterRegexRule(changes["repeatCharacter"].currentValue);
            inputChanged = changes["repeatCharacter"].isFirstChange() ? false : true;
        }
        if (changes["alphabeticalCharacter"]) {
            this.alphabeticalCharacterValidator = PasswordValidators.alphabeticalCharacterRule(changes["alphabeticalCharacter"].currentValue);
            inputChanged = changes["alphabeticalCharacter"].isFirstChange() ? false : true;
        }
        if (changes["digitCharacter"]) {
            this.digitCharacterValidator = PasswordValidators.digitCharacterRule(changes["digitCharacter"].currentValue);
            inputChanged = changes["digitCharacter"].isFirstChange() ? false : true;
        }
        if (changes["lowercaseCharacter"]) {
            this.lowercaseCharacterValidator = PasswordValidators.lowercaseCharacterRule(changes["lowercaseCharacter"].currentValue);
            inputChanged = changes["lowercaseCharacter"].isFirstChange() ? false : true;
        }
        if (changes["uppercaseCharacter"]) {
            this.uppercaseCharacterValidator = PasswordValidators.uppercaseCharacterRule(changes["uppercaseCharacter"].currentValue);
            inputChanged = changes["uppercaseCharacter"].isFirstChange() ? false : true;
        }
        if (inputChanged && this.onChange) {
            this.onChange();
        }
    }
    validate(c) {
        const compose = Validators.compose([
            this.repeatCharacterValidator,
            this.digitCharacterValidator,
            this.alphabeticalCharacterValidator,
            this.lowercaseCharacterValidator,
            this.uppercaseCharacterValidator,
        ]);
        return compose ? compose(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
PasswordValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PasswordValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
PasswordValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: PasswordValidatorDirective, selector: "[password][formControlName],[password][formControl],[password][ngModel]", inputs: { repeatCharacter: "repeatCharacter", alphabeticalCharacter: "alphabeticalCharacter", digitCharacter: "digitCharacter", lowercaseCharacter: "lowercaseCharacter", uppercaseCharacter: "uppercaseCharacter" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => PasswordValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: PasswordValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[password][formControlName],[password][formControl],[password][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => PasswordValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { repeatCharacter: [{
                type: Input
            }], alphabeticalCharacter: [{
                type: Input
            }], digitCharacter: [{
                type: Input
            }], lowercaseCharacter: [{
                type: Input
            }], uppercaseCharacter: [{
                type: Input
            }] } });

class EmailValidatorDirective {
    constructor() {
        this.email = "normal";
    }
    ngOnInit() {
        this.setValidator(this.email);
    }
    setValidator(type) {
        switch (type) {
            case "simple":
                this.validator = EmailValidators.simple;
                break;
            case "normal":
                this.validator = EmailValidators.normal;
                break;
            default:
                this.validator = EmailValidators.normal;
                break;
        }
    }
    ngOnChanges(changes) {
        if (changes["email"] && !changes["email"].isFirstChange()) {
            this.setValidator(changes["email"].currentValue);
            if (this.onChange) {
                this.onChange();
            }
        }
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
}
EmailValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmailValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
EmailValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: EmailValidatorDirective, selector: "[email][formControlName],[email][formControl],[email][ngModel]", inputs: { email: "email" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => EmailValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmailValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[email][formControlName],[email][formControl],[email][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => EmailValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { email: [{
                type: Input
            }] } });
class EmailSuggestValidatorDirective {
    ngOnInit() {
        this.validator = EmailValidators.suggest(this.emailSuggest);
    }
    ngOnChanges(changes) {
        if (changes["emailSuggest"] && !changes["emailSuggest"].isFirstChange()) {
            this.validator = EmailValidators.suggest(changes["emailSuggest"].currentValue);
            if (this.onChange) {
                this.onChange();
            }
        }
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
}
EmailSuggestValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmailSuggestValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
EmailSuggestValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: EmailSuggestValidatorDirective, selector: "[emailSuggest][formControlName],[emailSuggest][formControl],[emailSuggest][ngModel]", inputs: { emailSuggest: "emailSuggest" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => EmailSuggestValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmailSuggestValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[emailSuggest][formControlName],[emailSuggest][formControl],[emailSuggest][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => EmailSuggestValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { emailSuggest: [{
                type: Input
            }] } });

class WhiteSpaceValidatorDirective {
    constructor() {
        this.validator = UniversalValidators.noWhitespace;
    }
    validate(c) {
        return this.validator(c);
    }
}
WhiteSpaceValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: WhiteSpaceValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
WhiteSpaceValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: WhiteSpaceValidatorDirective, selector: "[noWhitespace][formControlName],[noWhitespace][formControl],[noWhitespace][ngModel]", providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => WhiteSpaceValidatorDirective),
            multi: true,
        },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: WhiteSpaceValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[noWhitespace][formControlName],[noWhitespace][formControl],[noWhitespace][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => WhiteSpaceValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }] });
class EmptyStringValidatorDirective {
    constructor() {
        this.validator = UniversalValidators.noEmptyString;
    }
    validate(c) {
        return this.validator(c);
    }
}
EmptyStringValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmptyStringValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
EmptyStringValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: EmptyStringValidatorDirective, selector: "[noEmptyString][formControlName],[noEmptyString][formControl],[noEmptyString][ngModel]", providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => EmptyStringValidatorDirective),
            multi: true,
        },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EmptyStringValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[noEmptyString][formControlName],[noEmptyString][formControl],[noEmptyString][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => EmptyStringValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }] });
class IsNumberValidatorDirective {
    constructor() {
        this.validator = UniversalValidators.isNumber;
    }
    validate(c) {
        return this.validator(c);
    }
}
IsNumberValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IsNumberValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
IsNumberValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: IsNumberValidatorDirective, selector: "[isNumber][formControlName],[isNumber][formControl],[isNumber][ngModel]", providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => IsNumberValidatorDirective),
            multi: true,
        },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IsNumberValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[isNumber][formControlName],[isNumber][formControl],[isNumber][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => IsNumberValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }] });
class IsInRangeValidatorDirective {
    constructor() {
        this.minValue = 0;
        this.maxValue = 0;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.isInRange(this.minValue, this.maxValue);
    }
    ngOnChanges(changes) {
        let minValue = this.minValue;
        let maxValue = this.maxValue;
        let changed = false;
        if (changes["minValue"]) {
            minValue = changes["minValue"].currentValue;
            changed = changes["minValue"].isFirstChange() ? false : true;
        }
        if (changes["maxValue"]) {
            maxValue = changes["maxValue"].currentValue;
            changed = changes["maxValue"].isFirstChange() ? false : true;
        }
        if (changed) {
            this.validator = UniversalValidators.isInRange(minValue, maxValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
IsInRangeValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IsInRangeValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
IsInRangeValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: IsInRangeValidatorDirective, selector: "[isInRange][formControlName],[isInRange][formControl],[isInRange][ngModel]", inputs: { minValue: "minValue", maxValue: "maxValue" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => IsInRangeValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: IsInRangeValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[isInRange][formControlName],[isInRange][formControl],[isInRange][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => IsInRangeValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { minValue: [{
                type: Input
            }], maxValue: [{
                type: Input
            }] } });
class MaxValidatorDirective {
    constructor() {
        this.max = 0;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.max(this.max);
    }
    ngOnChanges(changes) {
        if (changes["max"] && !changes["max"].isFirstChange()) {
            this.validator = UniversalValidators.max(changes["max"].currentValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
MaxValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MaxValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MaxValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: MaxValidatorDirective, selector: "input[type=text][max][formControlName],input[type=text][max][formControl],input[type=text][max][ngModel]", inputs: { max: "max" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => MaxValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MaxValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[type=text][max][formControlName],input[type=text][max][formControl],input[type=text][max][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => MaxValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { max: [{
                type: Input
            }] } });
class MinValidatorDirective {
    constructor() {
        this.min = 0;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.min(this.min);
    }
    ngOnChanges(changes) {
        if (changes["min"] && !changes["min"].isFirstChange()) {
            this.validator = UniversalValidators.min(changes["min"].currentValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
MinValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MinValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MinValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: MinValidatorDirective, selector: "input[type=text][min][formControlName],input[type=text][min][formControl],input[type=text][min][ngModel]", inputs: { min: "min" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => MinValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MinValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[type=text][min][formControlName],input[type=text][min][formControl],input[type=text][min][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => MinValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { min: [{
                type: Input
            }] } });
class MinDateValidatorDirective {
    constructor() {
        this.minDate = "";
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.minDate(new Date(this.minDate));
    }
    ngOnChanges(changes) {
        if (changes["minDate"] && !changes["minDate"].isFirstChange()) {
            this.validator = UniversalValidators.minDate(changes["min"].currentValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
MinDateValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MinDateValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MinDateValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: MinDateValidatorDirective, selector: "input[type=text][minDate][formControlName],input[type=text][minDate][formControl],input[type=text][minDate][ngModel]", inputs: { minDate: "minDate" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => MinDateValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MinDateValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[type=text][minDate][formControlName],input[type=text][minDate][formControl],input[type=text][minDate][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => MinDateValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { minDate: [{
                type: Input
            }] } });
class MaxDateValidatorDirective {
    constructor() {
        this.maxDate = "";
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.maxDate(new Date(this.maxDate));
    }
    ngOnChanges(changes) {
        if (changes["maxDate"] && !changes["maxDate"].isFirstChange()) {
            this.validator = UniversalValidators.maxDate(changes["min"].currentValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
MaxDateValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MaxDateValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
MaxDateValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: MaxDateValidatorDirective, selector: "input[type=text][maxDate][formControlName],input[type=text][maxDate][formControl],input[type=text][maxDate][ngModel]", inputs: { maxDate: "maxDate" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => MaxDateValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: MaxDateValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[type=text][maxDate][formControlName],input[type=text][maxDate][formControl],input[type=text][maxDate][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => MaxDateValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { maxDate: [{
                type: Input
            }] } });
class TypeValidatorDirective {
    constructor() {
        this.requiredType = "string";
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this.onChange = () => { };
    }
    ngOnInit() {
        this.validator = UniversalValidators.type(this.requiredType);
    }
    ngOnChanges(changes) {
        if (changes["requireType"] && !changes["requireType"].isFirstChange()) {
            this.validator = UniversalValidators.type(changes["requireType"].currentValue);
            this.onChange();
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
TypeValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TypeValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
TypeValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: TypeValidatorDirective, selector: "input[requireType][formControlName]", inputs: { requiredType: "requiredType" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => TypeValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: TypeValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "input[requireType][formControlName]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => TypeValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { requiredType: [{
                type: Input
            }] } });

class CreditCardValidatorDirective {
    constructor() {
        this.creditCard = "all";
    }
    ngOnInit() {
        this.setCreditcardValidator(this.creditCard);
    }
    setCreditcardValidator(type) {
        switch (type) {
            case "all":
                this.validator = CreditCardValidators.isCreditCard;
                break;
            case "americanExpress":
                this.validator = CreditCardValidators.americanExpress;
                break;
            case "dinersclub":
                this.validator = CreditCardValidators.dinersclub;
                break;
            case "discover":
                this.validator = CreditCardValidators.discover;
                break;
            case "jcb":
                this.validator = CreditCardValidators.jcb;
                break;
            case "maestro":
                this.validator = CreditCardValidators.maestro;
                break;
            case "mastercard":
                this.validator = CreditCardValidators.mastercard;
                break;
            case "visa":
                this.validator = CreditCardValidators.visa;
                break;
            default:
                this.validator = CreditCardValidators.isCreditCard;
                break;
        }
    }
    ngOnChanges(changes) {
        if (changes["creditCard"] && !changes["creditCard"].isFirstChange()) {
            this.setCreditcardValidator(changes["creditCard"].currentValue);
            if (this.onChange) {
                this.onChange();
            }
        }
    }
    validate(c) {
        return this.validator ? this.validator(c) : null;
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
CreditCardValidatorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CreditCardValidatorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
CreditCardValidatorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: CreditCardValidatorDirective, selector: "[creditCard][formControlName],[creditCard][formControl],[creditCard][ngModel]", inputs: { creditCard: "creditCard" }, providers: [
        {
            provide: NG_VALIDATORS,
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            useExisting: forwardRef(() => CreditCardValidatorDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: CreditCardValidatorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[creditCard][formControlName],[creditCard][formControl],[creditCard][ngModel]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // eslint-disable-next-line @typescript-eslint/no-use-before-define
                            useExisting: forwardRef(() => CreditCardValidatorDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { creditCard: [{
                type: Input
            }] } });

class EqualToDirective {
    constructor() {
        this.equalTo = "";
    }
    validate(c) {
        var _a;
        const otherControl = typeof this.equalTo === "string" ? (_a = c.parent) === null || _a === void 0 ? void 0 : _a.get(this.equalTo) : this.equalTo;
        if (!this.subscription) {
            this.subscription = otherControl === null || otherControl === void 0 ? void 0 : otherControl.valueChanges.pipe(delay(1)).subscribe(() => {
                c.updateValueAndValidity();
            });
        }
        return c.value !== (otherControl === null || otherControl === void 0 ? void 0 : otherControl.value) ? { notEqualTo: true } : null;
    }
    ngOnDestroy() {
        var _a;
        (_a = this.subscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
    }
    ngOnChanges(changes) {
        if (changes["equalTo"] && !changes["equalTo"].isFirstChange()) {
            if (this.onChange) {
                this.onChange();
            }
        }
    }
    registerOnValidatorChange(fn) {
        this.onChange = fn;
    }
}
EqualToDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EqualToDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
EqualToDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.0.1", type: EqualToDirective, selector: "[equalTo][ngModel], [equalTo][formControlName], [equalTo][formControl]", inputs: { equalTo: "equalTo" }, providers: [
        {
            provide: NG_VALIDATORS,
            // tslint:disable-next-line:no-forward-ref
            useExisting: forwardRef(() => EqualToDirective),
            multi: true,
        },
    ], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: EqualToDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[equalTo][ngModel], [equalTo][formControlName], [equalTo][formControl]",
                    providers: [
                        {
                            provide: NG_VALIDATORS,
                            // tslint:disable-next-line:no-forward-ref
                            useExisting: forwardRef(() => EqualToDirective),
                            multi: true,
                        },
                    ],
                }]
        }], propDecorators: { equalTo: [{
                type: Input
            }] } });

class ValidatorsModule {
}
ValidatorsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidatorsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ValidatorsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidatorsModule, declarations: [CreditCardValidatorDirective,
        EmailValidatorDirective,
        EmailSuggestValidatorDirective,
        PasswordValidatorDirective,
        IsInRangeValidatorDirective,
        IsNumberValidatorDirective,
        MaxValidatorDirective,
        MinValidatorDirective,
        MinDateValidatorDirective,
        MaxDateValidatorDirective,
        WhiteSpaceValidatorDirective,
        EmptyStringValidatorDirective,
        EqualToDirective,
        TypeValidatorDirective], exports: [CreditCardValidatorDirective,
        EmailValidatorDirective,
        EmailSuggestValidatorDirective,
        PasswordValidatorDirective,
        IsInRangeValidatorDirective,
        IsNumberValidatorDirective,
        MaxValidatorDirective,
        MinValidatorDirective,
        MinDateValidatorDirective,
        MaxDateValidatorDirective,
        WhiteSpaceValidatorDirective,
        EmptyStringValidatorDirective,
        EqualToDirective,
        TypeValidatorDirective] });
ValidatorsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidatorsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.0.1", ngImport: i0, type: ValidatorsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        CreditCardValidatorDirective,
                        EmailValidatorDirective,
                        EmailSuggestValidatorDirective,
                        PasswordValidatorDirective,
                        IsInRangeValidatorDirective,
                        IsNumberValidatorDirective,
                        MaxValidatorDirective,
                        MinValidatorDirective,
                        MinDateValidatorDirective,
                        MaxDateValidatorDirective,
                        WhiteSpaceValidatorDirective,
                        EmptyStringValidatorDirective,
                        EqualToDirective,
                        TypeValidatorDirective,
                    ],
                    exports: [
                        CreditCardValidatorDirective,
                        EmailValidatorDirective,
                        EmailSuggestValidatorDirective,
                        PasswordValidatorDirective,
                        IsInRangeValidatorDirective,
                        IsNumberValidatorDirective,
                        MaxValidatorDirective,
                        MinValidatorDirective,
                        MinDateValidatorDirective,
                        MaxDateValidatorDirective,
                        WhiteSpaceValidatorDirective,
                        EmptyStringValidatorDirective,
                        EqualToDirective,
                        TypeValidatorDirective,
                    ],
                }]
        }] });

class EqualToValidator {
    static equalTo(c1Name, c2Name) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const validator = (parent) => {
            const c1 = parent.get(c1Name);
            const c2 = parent.get(c2Name);
            if ((c1 === null || c1 === void 0 ? void 0 : c1.value) === (c2 === null || c2 === void 0 ? void 0 : c2.value)) {
                AbstractControlUtil.removeError(c2, "notEqualTo");
            }
            else {
                AbstractControlUtil.addError(c2, "notEqualTo", true);
            }
        };
        return validator;
    }
}

// validators

/**
 * Generated bundle index. Do not edit.
 */

export { AbstractControlUtil, CreditCardValidatorDirective, CreditCardValidators, EmailSuggestValidatorDirective, EmailSuggestion, EmailValidatorDirective, EmailValidators, EmptyStringValidatorDirective, EqualToDirective, EqualToValidator, IsInRangeValidatorDirective, IsNumberValidatorDirective, MaxDateValidatorDirective, MaxValidatorDirective, MinDateValidatorDirective, MinValidatorDirective, PasswordValidatorDirective, PasswordValidators, TypeValidatorDirective, UniversalValidators, ValidatorsModule, WhiteSpaceValidatorDirective };
